import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { fetchPublicBible, bibleImageUrl } from "../lib/api";

/**
 * /list/<id> — public brutalist render of a saved curated list.
 *
 * The PNG variant at /api/og/bible/<id>.png remains for social cards;
 * this page is the clickable HTML twin. When someone shares this URL on
 * X / LinkedIn / etc, the platform's crawler reads the OG meta below and
 * renders a card that links back here — so clicking the card on social
 * lands the user on this page, and every tool listed is a blue link to
 * its detail page.
 */
export default function ListView() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData(null);
    setErr("");
    fetchPublicBible(id)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "list not found"));
  }, [id]);

  if (err) {
    return (
      <div data-testid="list-view-not-found">
        <Header />
        <main className="max-w-5xl mx-auto px-4 py-10">
          <h1 className="text-[18px] font-bold">list not found</h1>
          <p className="mt-2 text-[13px]">
            <Link to="/" className="tv-link">[browse the full index →]</Link>
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <Header />
        <main className="max-w-5xl mx-auto px-4 py-10 text-[13px] text-[#888]">
          loading list<span className="tv-blink">_</span>
        </main>
        <Footer />
      </div>
    );
  }

  const { title, subtitle, modules, total, n_modules, footer_cta } = data;
  const seoDescription =
    subtitle ||
    `${total} hand-picked investing tools across ${n_modules} sections. terminalvalue.io.`;

  return (
    <div data-testid="list-view">
      <Seo
        title={`${title} — TerminalValue.io`}
        description={seoDescription}
        path={`/list/${id}`}
        image={bibleImageUrl(id)}
      />
      <Header />

      <section className="border-b border-black bg-white">
        <div className="max-w-5xl mx-auto px-4 py-5">
          <h1 className="text-[20px] font-bold leading-snug" data-testid="list-view-title">
            {">_"} {title}
          </h1>
          {subtitle && (
            <p className="mt-1 text-[12px] text-[#666]" data-testid="list-view-subtitle">
              {subtitle}
            </p>
          )}
          <p className="mt-2 text-[12px] text-[#666]">
            <span className="tv-hl">{total}</span> tools across{" "}
            <span className="tv-hl">{n_modules}</span> sections ·{" "}
            <Link to="/" className="tv-link">[browse the full index →]</Link>
          </p>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {modules
          .filter((m) => m.entries && m.entries.length > 0)
          .map((m, mi) => (
            <section
              key={mi}
              className="mb-6"
              data-testid={`list-view-module-${mi}`}
            >
              <h2 className="text-[14px] font-bold mb-2">
                ## {m.name}{" "}
                <span className="text-[#888] font-normal">({m.entries.length})</span>
              </h2>
              <ol className="text-[13px] leading-[1.65] list-none p-0">
                {m.entries.map((e, ei) => (
                  <li
                    key={`${mi}-${ei}`}
                    className="flex items-baseline gap-2"
                    data-testid={`list-view-entry-${mi}-${ei}`}
                  >
                    <span className="text-[#888] tabular-nums w-7 text-right shrink-0">
                      {String(e.n).padStart(2, "0")}
                    </span>
                    <Link
                      to={`/tool/${e.slug}`}
                      className="tv-link font-bold"
                    >
                      {e.title}
                    </Link>
                    {e.domain && (
                      <span className="text-[#888]">— {e.domain}</span>
                    )}
                    {e.meta && (
                      <span className="text-[#aaa]">· {e.meta}</span>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          ))}

        <div className="mt-10 pt-4 border-t border-[#ddd] text-[12px] text-[#666]">
          {footer_cta && <p className="mb-2">{footer_cta}</p>}
          <p>
            <Link to="/" className="tv-link">[index →]</Link>{" "}
            ·{" "}
            <Link to="/ask" className="tv-link">[ask the concierge →]</Link>{" "}
            ·{" "}
            <Link to="/submit" className="tv-link">[submit a tool →]</Link>
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
